import React, { Fragment, useCallback } from 'react';
import { styled, SxProp } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useNavigation, useRoute } from '@react-navigation/native';
import { View } from 'react-native';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

import {
  screens,
  SearchTabNavigatorNavigationPropType,
  SearchTabNavigatorRoutePropType,
} from '@navigation';
import {
  BaseText,
  CollapsibleTabListScene,
  EmptyResultTypeEnum,
  EmptyResultView,
  ScreenTopBar,
  Separator,
  TutorialList,
} from '@components';
import { IS_WEB, passIf } from '@utils';
import { useAsyncInit, useSearch } from '@hooks';
import { useTutorialsModel } from '@models/Tutorials/TutorialsModel';
import type { Tutorial } from '@models/Tutorials/TutorialModel';
import { useViewerModel } from '@models/Viewer/ViewerModel';

const Container = styled(View)({
  flex: 1,
  bg: '$background0',
});

const HeaderTitle = styled(BaseText)({
  color: '$heading800',
  fontWeight: 'semi',
  variant: 'text.xxlarge',
});

const ListWebContainer = styled(View)({
  flex: 1,
  mx: [null, '5%'],
});

const ListContainer = IS_WEB
  ? ListWebContainer
  : CollapsibleTabListScene;

function contentContainerStyle(props: EdgeInsets): SxProp {
  return (theme) => ({
    flexGrow: 1,
    px: ['$6', '$0'],
    pb: theme.space.$6 + props.bottom,
  });
}

type NavigationPropType =
  SearchTabNavigatorNavigationPropType<'SearchTutorial'>;
type RoutePropType =
  SearchTabNavigatorRoutePropType<'SearchTutorial'>;

export const SearchTutorialScreen = observer(() => {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const route = useRoute<RoutePropType>();
  const nav = useNavigation<NavigationPropType>();
  const viewer = useViewerModel();
  const tutorials = useTutorialsModel();
  const { performFetch, updateSearchQuery, query, store } =
    useSearch();

  useAsyncInit(() => fetch(), [query]);

  async function fetch() {
    await performFetch(tutorials.searched.fetch.run);
  }

  async function fetchMore() {
    await performFetch(tutorials.searched.fetchMore.run);
  }

  async function refresh() {
    await performFetch((q) =>
      Promise.all([
        tutorials.searched.fetch.run(q),
        viewer.usersSearch.fetch.run(q),
      ]),
    );
  }

  const navigateToTutorial = useCallback((item: Tutorial) => {
    nav.push(screens.HubNavigator, {
      screen: screens.Tutorial,
      params: {
        id: item.id,
      },
    });
  }, []);

  const numColumns = IS_WEB ? 4 : 2;

  const emptyResultType = tutorials.searched.fetch.isError
    ? EmptyResultTypeEnum.Error
    : EmptyResultTypeEnum.NoResult;

  const isNoResult = emptyResultType === EmptyResultTypeEnum.NoResult;

  const isLoading =
    tutorials.searched.fetch.inProgress && tutorials.searched.isEmpty;

  return (
    <Container>
      {IS_WEB && (
        <Fragment>
          <ScreenTopBar
            title={t('search.title')}
            initSearchText={query}
            onSearchChange={updateSearchQuery}
          />
          <Separator size="$8" />
        </Fragment>
      )}

      <ListContainer name={route.name}>
        <TutorialList
          numColumns={numColumns}
          contentContainerSx={contentContainerStyle(insets)}
          ListHeaderComponent={
            <Fragment>
              {IS_WEB && (
                <HeaderTitle>{t('search.video')}</HeaderTitle>
              )}
              <Separator size="$8" />
            </Fragment>
          }
          data={tutorials.searched.asArray}
          onPress={navigateToTutorial}
          onEndReached={fetchMore}
          isLoading={isLoading}
          onRefresh={refresh}
          extraStore={store}
          ListEmptyComponent={() => (
            <EmptyResultView
              type={emptyResultType}
              description={passIf(
                isNoResult,
                t('search.emptySearchResult'),
              )}
            />
          )}
        />
      </ListContainer>
    </Container>
  );
});
